<script setup>
import {
  ref,
  computed,
  onMounted,
  getCurrentInstance,
} from 'vue';
import { INPUT_PROPS } from '@/composables/genericProps';
import FormErrorList from '@/components/form/FormErrorList.vue';

const instance = getCurrentInstance();
const emit = defineEmits(['update:modelValue']);
const isFocused = ref(false)
const props = defineProps({
  ...INPUT_PROPS,
  type: {
    type: String,
    default: 'text',
  },
  isLarge: {
    type: Boolean,
    default: false,
  },
  maxLength: {
    type: Number,
    default: null,
  },
  minLength: {
    type: Number,
    default: null,
  },
  pattern: {
    type: String,
    default: null,
  },
  labelPosition: {
    type: String,
    default: 'inside',
    validator: (value) => ['inside', 'top'].includes(value),
  },
  currency: {
    type: String,
    default: null,
    validator: (value) => value === null || value === 'euro',
  },
  icon: {
    description: 'Font Awesome name shown inside the field, before the text. E.g. "search".',
    type: String,
    default: '',
  },
});

const givenId = computed(() => `input-${instance.uid}`);
const isTopLabel = computed(() => props.labelPosition === 'top');
const inputId = computed(() => props.id || givenId.value);
const hasValue = computed(() => (
  props.modelValue !== null
  && props.modelValue !== undefined
  && String(props.modelValue) !== ''
));

const inputGroupClass = computed(() => {
  if (isTopLabel.value) {
    return {};
  }
  return { 'is-focused': isFocused.value };
});
const hasCurrency = computed(() => Boolean(props.currency));
const currencyPrefix = computed(() => {
  if (props.currency === 'euro') {
    return '€';
  }
  return '';
});
const resolvedType = computed(() => (hasCurrency.value ? 'number' : props.type));
const resolvedPlaceholder = computed(() => {
  if (!props.placeholder) {
    return '';
  }
  // Without a label the placeholder is the only text naming the field, so it
  // carries the required marker the label would otherwise show.
  if (!props.label) {
    return `${instance.proxy.$t(props.placeholder)}${props.required ? ' *' : ''}`;
  }
  if (isTopLabel.value) {
    return instance.proxy.$t(props.placeholder);
  }
  return isFocused.value ? instance.proxy.$t(props.placeholder) : '';
});

const handleInput = (event) => {
  emit('update:modelValue', event.target.value);
};

onMounted(() => {
  if (props.autoFocus) {
    const input = document.getElementById(inputId.value);
    input.focus();
  }

  if (props.autoSelect) {
    const input = document.getElementById(inputId.value);
    input.select();
  }
});
</script>

<template>
  <div v-if="isTopLabel" :class="{ 'mb-2': !noMargin }">
    <label v-if="props.label" class="form-label input-label-top" :for="inputId">
      {{ $t(props.label) }}<span v-if="required">*</span>
    </label>
    <div class="input-group input-group-static">
      <span v-if="hasCurrency" class="input-group-text currency-prefix">{{ currencyPrefix }}</span>
      <span
        v-if="icon"
        class="input-group-text input-icon"
      >
        <i
          class="fa"
          :class="`fa-${icon}`"
          aria-hidden="true"
        />
      </span>
      <input
        :id="inputId"
        :disabled="disabled"
        class="form-control"
        :class="{ 'form-control--large': isLarge, 'form-control--with-icon': Boolean(icon) }"
        :placeholder="resolvedPlaceholder"
        :type="resolvedType"
        :step="hasCurrency ? '0.01' : null"
        :inputmode="hasCurrency ? 'decimal' : null"
        :value="modelValue"
        :maxlength="maxLength || null"
        :minlength="minLength || null"
        :pattern="pattern || null"
        @input="handleInput"
        @focus="isFocused = true"
        @blur="isFocused = false"
        :required="required"
      />
      <!--
        Voor een knop die tegen het veld aan hoort te staan -- zoeken, inschrijven.
        Binnen de input-group houdt Bootstrap beide even hoog; ernaast in een
        d-flex wordt het een veld van 46px naast een knop van 36px.
      -->
      <slot name="append"></slot>
    </div>
  </div>
  <div
    v-else
    class="input-group input-group-dynamic"
    :class="[inputGroupClass, { 'mb-2': !noMargin, 'has-value': hasValue, 'has-floating-label': Boolean(props.label) }]"
  >
    <label v-if="props.label" class="form-label" :for="inputId">
      {{ $t(props.label) }}<span v-if="required">*</span>
    </label>
    <span v-if="hasCurrency" class="input-group-text currency-prefix">{{ currencyPrefix }}</span>
    <span
      v-if="icon"
      class="input-group-text input-icon"
    >
      <i
        class="fa"
        :class="`fa-${icon}`"
        aria-hidden="true"
      />
    </span>
    <input
      :id="inputId"
      :disabled="disabled"
      class="form-control"
      :class="{ 'form-control--large': isLarge, 'form-control--with-icon': Boolean(icon) }"
      :placeholder="resolvedPlaceholder"
      :type="resolvedType"
      :step="hasCurrency ? '0.01' : null"
      :inputmode="hasCurrency ? 'decimal' : null"
      :value="modelValue"
      :maxlength="maxLength || null"
      :minlength="minLength || null"
      :pattern="pattern || null"
      @input="handleInput"
      @focus="isFocused = true"
      @blur="isFocused = false"
      :required="required"
    />
    <slot name="append"></slot>
  </div>
  <form-error-list :errors="errors" />
</template>

<style scoped>
.input-label-top {
  position: static;
  display: block;
  margin-bottom: 0.35rem;
}

.form-control--large {
  font-size: 1.2rem;
  font-weight: 700;
}

.currency-prefix {
  border-right: 0;
}

.currency-prefix + .form-control {
  border-left: 0;
}

.input-icon {
  position: absolute;
  left: 0.7rem;
  right: auto;
  width: auto;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--grey-color);
  pointer-events: none;
}

.input-group.input-group-dynamic input.form-control--with-icon,
.input-group.input-group-static input.form-control--with-icon {
  padding-left: 2.35rem !important;
}
</style>
